/**
 * useVersionCheck — detects when a newer SPA artifact has been deployed.
 *
 * How it works
 * ------------
 * At build time Vite writes `dist/version.json` (`{ version, buildTime, … }`)
 * and inlines the identical `version` string into the bundle as
 * `__APP_VERSION__` (see vite.config.ts). The running app boots pinned to that
 * value. This hook periodically fetches `/version.json` (cache-busted,
 * `no-store`, and excluded from the service-worker cache) and, when the served
 * `version` differs from the one the app booted with, reports
 * `updateAvailable = true`.
 *
 * The poll runs on an interval (~3 min by default) and opportunistically on
 * window focus / tab visibility so a user returning to a long-lived tab learns
 * about a fresh deploy quickly. All network/parse errors are swallowed — a
 * failed check never disrupts the app; it simply retries on the next tick.
 */

import { useCallback, useEffect, useRef, useState } from 'react';

/** Version the app was built/booted with (inlined by Vite). */
export const BOOT_VERSION: string = typeof __APP_VERSION__ === 'string' ? __APP_VERSION__ : 'dev';

const DEFAULT_POLL_INTERVAL_MS = 3 * 60 * 1000; // 3 minutes
const VERSION_URL = '/version.json';

export interface VersionCheckOptions {
  /** Milliseconds between background polls. Default: 3 min. */
  pollIntervalMs?: number;
  /** Disable the hook entirely (e.g. under test). Default: false. */
  disabled?: boolean;
}

export interface VersionCheckState {
  /** True once a deployed version different from BOOT_VERSION is observed. */
  updateAvailable: boolean;
  /** The most recently fetched remote version (null until first success). */
  latestVersion: string | null;
  /** The version the app booted with. */
  bootVersion: string;
  /** Force an immediate check (returns the fetched version, or null on error). */
  checkNow: () => Promise<string | null>;
}

/**
 * Fetch the currently-deployed version marker. Cache-busted + `no-store` so we
 * never read a stale copy. Returns null on any network/parse error (silent).
 */
export async function fetchDeployedVersion(signal?: AbortSignal): Promise<string | null> {
  try {
    const res = await fetch(`${VERSION_URL}?ts=${Date.now()}`, {
      cache: 'no-store',
      headers: { 'cache-control': 'no-cache' },
      signal,
    });
    if (!res.ok) return null;
    const data = (await res.json()) as { version?: unknown };
    return typeof data.version === 'string' && data.version.length > 0 ? data.version : null;
  } catch {
    return null;
  }
}

/**
 * Best-effort hard reload: activate any waiting service worker, drop the SPA
 * shell caches so the newest hashed assets are fetched, then reload the page.
 * Cache clearing is optional and fully guarded — a hostile/absent Cache API or
 * SW never blocks the reload.
 */
export async function hardReload(): Promise<void> {
  try {
    if ('serviceWorker' in navigator) {
      const reg = await navigator.serviceWorker.getRegistration();
      reg?.waiting?.postMessage('SKIP_WAITING');
    }
  } catch {
    /* ignore */
  }
  try {
    if (typeof caches !== 'undefined') {
      const keys = await caches.keys();
      await Promise.all(keys.filter((k) => k.startsWith('psn-')).map((k) => caches.delete(k)));
    }
  } catch {
    /* ignore */
  }
  window.location.reload();
}

export function useVersionCheck(options: VersionCheckOptions = {}): VersionCheckState {
  const { pollIntervalMs = DEFAULT_POLL_INTERVAL_MS, disabled = false } = options;

  const [latestVersion, setLatestVersion] = useState<string | null>(null);
  const [updateAvailable, setUpdateAvailable] = useState(false);

  // Guard against overlapping fetches and post-unmount state updates.
  const inFlight = useRef(false);
  const mounted = useRef(true);

  const checkNow = useCallback(async (): Promise<string | null> => {
    if (inFlight.current) return null;
    inFlight.current = true;
    try {
      const version = await fetchDeployedVersion();
      if (!mounted.current || version === null) return version;
      setLatestVersion(version);
      if (version !== BOOT_VERSION) setUpdateAvailable(true);
      return version;
    } finally {
      inFlight.current = false;
    }
  }, []);

  useEffect(() => {
    mounted.current = true;
    if (disabled) return undefined;

    // Kick off one check shortly after mount, then on the interval.
    void checkNow();
    const timer = window.setInterval(() => void checkNow(), pollIntervalMs);

    const onVisible = () => {
      if (document.visibilityState === 'visible') void checkNow();
    };
    const onFocus = () => void checkNow();

    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('focus', onFocus);

    return () => {
      mounted.current = false;
      window.clearInterval(timer);
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('focus', onFocus);
    };
  }, [checkNow, pollIntervalMs, disabled]);

  return { updateAvailable, latestVersion, bootVersion: BOOT_VERSION, checkNow };
}
